/**
 * Lawn Care Estimator - Complete Application
 * All functionality in one file for simplicity
 */

// Global variables
let estimatedSqFt = 0;
let estimatedPrice = 45;
let drawingActive = false;
let drawingMode = 'freehand'; // 'pin' or 'freehand' - default to freehand
let isDrawingFreehand = false;
let map, geocoder;
let markers = [], path = [], polyline = null, polygon = null, serviceAddress = '';
let freehandPoints = [];
let drawingCanvas = null;
let drawingContext = null;
let mapBounds = null;
let mapZoom = null;

// Haptic feedback variables
let hapticInterval = null;
let hapticStartTime = null;

// Haptic feedback functions
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

function startHapticFeedback() {
  // Only provide haptic feedback on mobile devices
  if (!isMobileDevice()) return;
  
  hapticStartTime = Date.now();
  
  // Check if vibration API is available
  if (navigator.vibrate) {
    // Start continuous low vibration pattern (50ms on, 50ms off)
    hapticInterval = setInterval(() => {
      // Stop after 5 seconds max
      if (Date.now() - hapticStartTime > 5000) {
        stopHapticFeedback();
        return;
      }
      navigator.vibrate(50);
    }, 100);
  }
}

function stopHapticFeedback() {
  if (hapticInterval) {
    clearInterval(hapticInterval);
    hapticInterval = null;
  }
  
  // Stop any ongoing vibration
  if (navigator.vibrate) {
    navigator.vibrate(0);
  }
  
  hapticStartTime = null;
}

// Configuration
const CONFIG = {
  GOOGLE_MAPS_API_KEY: 'AIzaSyA6y3SHrRqgeYakA2U1vKTi7PWnOC0ML3M', // Replace with your actual API key
  BASE_PRICE: 45,
  AREA_CUTOFF: 2400,
  ADDITIONAL_RATE: 0.02,
  // Service Area Zip Codes
  SERVICE_ZIP_CODES: new Set([
    '30363', '31141', '31145', '31146', '30309', '30361', '30332', '30308', '30313', '30306',
    '30369', '30324', '30303', '30318', '39901', '30305', '31106', '31107', '31126', '31131',
    '31139', '31150', '31156', '31192', '31193', '30301', '30302', '30325', '30343', '30348',
    '30353', '30355', '30357', '30358', '30364', '30368', '30370', '30371', '30374', '30375',
    '30377', '30380', '30384', '30385', '30388', '30392', '30394', '30396', '30398', '30334',
    '30314', '30312', '30333', '30307', '30359', '30322', '30326', '31119', '30329', '30310',
    '30327', '30317', '30031', '30036', '30037', '30315', '30030', '30342', '30311', '30316',
    '30033', '30339', '30319', '30345', '30032', '30080', '30079', '30002', '30366', '30082',
    '30341', '30126', '30021', '30081', '30354', '30344', '30346', '30304', '30328', '30336',
    '30362', '30378', '30067', '30331', '30321', '30288', '30320', '30340', '30069', '30072',
    '30168', '30085', '30086', '30034', '30337', '30084', '30083', '30338', '30035', '30298',
    '31136', '30356', '30360', '30297', '30060', '30068', '30088', '30272', '30008', '30106',
    '30294', '30350', '30349', '30090', '30061', '30063', '30065', '30006', '30007', '30122',
    '30093', '30091', '30287', '30071', '30260', '30003', '30010'
  ])
};

// SVG flag icon for markers
const svgFlag = `
  <svg width="300" height="200" viewBox="0 0 300 200"
       xmlns="http://www.w3.org/2000/svg"
       style="background:transparent;filter:drop-shadow(0 0 4px rgba(0,0,0,0.7));">
    <rect x="20" y="20" width="8" height="160"
          fill="white" stroke="#ccc" stroke-width="1"/>
    <polygon points="28,20 28,80 180,50"
             fill="#FFD700" stroke="#E6C200" stroke-width="1"/>
  </svg>`;
const flagIcon = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgFlag);

// Initialize everything when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  initializeApp();
  loadGoogleMaps();
});

// Main app initialization
function initializeApp() {
  initToggleAreaButton();
  initBannerDismissal();
  initBookingOverlay();
  initVideoModal();
  hideShopifyElements();
  initDrawingButtonVisibility();
  initInfoBarTooltips();
}

// Toggle area button
function initToggleAreaButton() {
  const toggleBtn = document.getElementById('toggleAreaBtn');
  const pinModeBtn = document.getElementById('pinModeBtn');
  const freehandModeBtn = document.getElementById('freehandModeBtn');
  
  // Main drawing toggle button
  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (drawingActive) {
      // Force end any active freehand drawing
      if (drawingMode === 'freehand' && isDrawingFreehand) {
        endCanvasDrawing({ preventDefault: () => {} });
      }
      finishDrawing();
    } else {
      clearShape();
      startDrawing();
    }
  });
  
  // Pin mode button
  if (pinModeBtn) {
    pinModeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      switchToMode('pin');
    });
  }
  
  // Freehand mode button  
  if (freehandModeBtn) {
    freehandModeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      switchToMode('freehand');
    });
  }
}

// Switch between drawing modes
function switchToMode(newMode) {
  if (drawingMode === newMode) return; // Already in this mode
  
  const pinModeBtn = document.getElementById('pinModeBtn');
  const freehandModeBtn = document.getElementById('freehandModeBtn');
  
  // Clear any existing shapes/progress when switching modes
  if (drawingActive || markers.length > 0 || freehandPoints.length > 0 || polygon || polyline) {
    // Force end any active drawing
    if (isDrawingFreehand) {
      endCanvasDrawing({ preventDefault: () => {} });
    }
    clearShape();
  }
  
  // Update mode
  drawingMode = newMode;
  
  // Update button states
  pinModeBtn.classList.toggle('active', newMode === 'pin');
  freehandModeBtn.classList.toggle('active', newMode === 'freehand');
}

// Manage drawing button visibility
function initDrawingButtonVisibility() {
  const drawingButtons = document.getElementById('drawingModeButtons');
  const overlay = document.getElementById('overlay');
  const servicesOverlay = document.getElementById('servicesOverlay');
  const searchInput = document.getElementById('topSearch');
  
  function updateDrawingButtonVisibility() {
    const overlayVisible = overlay && !overlay.classList.contains('hidden');
    const servicesVisible = servicesOverlay && !servicesOverlay.classList.contains('hidden');
    
    if (drawingButtons) {
      drawingButtons.style.display = (overlayVisible || servicesVisible) ? 'none' : 'flex';
    }
    
    // Hide/show search bar based on services overlay
    if (searchInput && searchInput.classList.contains('positioned')) {
      searchInput.style.display = servicesVisible ? 'none' : 'block';
    }
  }
  
  // Initial check
  updateDrawingButtonVisibility();
  
  // Create observer to watch for class changes
  const observer = new MutationObserver(updateDrawingButtonVisibility);
  
  if (overlay) {
    observer.observe(overlay, { attributes: true, attributeFilter: ['class'] });
  }
  
  if (servicesOverlay) {
    observer.observe(servicesOverlay, { attributes: true, attributeFilter: ['class'] });
  }
}

// Service area validation
function isWithinServiceArea(place) {
  // Extract zip code from the place object
  if (!place.address_components) return false;
  
  const zipComponent = place.address_components.find(component => 
    component.types.includes('postal_code')
  );
  
  if (!zipComponent) return false;
  
  const zipCode = zipComponent.short_name;
  return CONFIG.SERVICE_ZIP_CODES.has(zipCode);
}

function showServiceAreaError(address) {
  showBanner(
    `Sorry, we don't currently service that area. We provide lawn care services in select Atlanta metro zip codes.`,
    'warning'
  );
}

// InfoBar tooltip system
function initInfoBarTooltips() {
  updateInfoBarTooltip('initial');
}

function updateInfoBarTooltip(state, data = {}) {
  const infoBar = document.getElementById('infoBar');
  if (!infoBar) return;
  
  switch (state) {
    case 'initial':
      infoBar.textContent = 'Area: 0 sq ft | Estimated Price: $0';
      break;
      
    case 'address-selected':
      infoBar.textContent = 'Pan to the property and tap "DRAW AREA"';
      break;
      
    case 'drawing-freehand':
      infoBar.textContent = 'Use finger to draw a perimeter around the service area';
      break;
      
    case 'drawing-pins':
      infoBar.textContent = 'Tap to set pins and define the area that needs to be serviced';
      break;
      
    case 'area-complete':
      infoBar.textContent = 'Tap "DRAW AREA" to start over or "Choose Services" to book';
      break;
      
    case 'area-calculated':
      const { sqFt, price } = data;
      infoBar.textContent = `Area: ${sqFt} sq ft | Estimated Price: $${price}`;
      // After showing calculation briefly, switch to completion message
      setTimeout(() => updateInfoBarTooltip('area-complete'), 3000);
      break;
      
    default:
      infoBar.textContent = 'Area: 0 sq ft | Estimated Price: $0';
  }
}

// Lot lines button
// Banner dismissal
function initBannerDismissal() {
  const bannerClose = document.getElementById('bannerClose');
  
  bannerClose.addEventListener('click', () => {
    document.getElementById('banner').classList.add('hidden');
  });
}

// Hide Shopify elements
function hideShopifyElements() {
  function removeShopifyElements() {
    const selectors = [
      '.chat-toggle',
      'button[data-spec="toggle-button"]',
      'sticky-header.header-wrapper.color-background-1.gradient',
      '.footer',
      '.newsletter'
    ];
    
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(element => {
        element.remove();
      });
    });
  }

  removeShopifyElements();
  const observer = new MutationObserver(removeShopifyElements);
  observer.observe(document.body, { childList: true, subtree: true });
  setInterval(removeShopifyElements, 1000);
}

// Load Google Maps
function loadGoogleMaps() {
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${CONFIG.GOOGLE_MAPS_API_KEY}&libraries=places,geometry&callback=initMap`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

// Initialize Google Maps
function initMap() {
  geocoder = new google.maps.Geocoder();
  
  map = new google.maps.Map(document.getElementById('map'), {
    center: { lat: 0, lng: 0 }, // Start at world center instead of SF
    zoom: 1, // Start very zoomed out so nothing specific is visible
    mapTypeId: 'hybrid',
    tilt: 0,
    rotateControl: false,
    fullscreenControl: true,
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: google.maps.ControlPosition.TOP_RIGHT
    },
    fullscreenControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
    zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM }
  });

  initAutocomplete();
  initMapEventListeners();
}

// Autocomplete setup
function initAutocomplete() {
  const topAutocomplete = new google.maps.places.Autocomplete(
    document.getElementById('topSearch'),
    {
      componentRestrictions: { country: 'us' }
    }
  );
  topAutocomplete.addListener('place_changed', () => {
    const place = topAutocomplete.getPlace();
    if (place.geometry) onAddressChosen(place, true);
  });
}

// Map event listeners
function initMapEventListeners() {
  document.getElementById('statusBar').addEventListener('click', () => {
    const statusBar = document.getElementById('statusBar');
    if (!statusBar.classList.contains('disabled')) {
      showServicesOverlay();
    }
  });

  document.getElementById('closeServicesOverlay').addEventListener('click', () => {
    document.getElementById('servicesOverlay').classList.add('hidden');
  });
}

// Map functions
function setMapCursor(cursor) {
  const mapDiv = map.getDiv();
  if (cursor) {
    mapDiv.classList.add('drawing-cursor');
  } else {
    mapDiv.classList.remove('drawing-cursor');
  }
}

function onAddressChosen(place, fromOverlay) {
  // Check if location is within service area by zip code
  if (!isWithinServiceArea(place)) {
    const address = place.formatted_address || 'this location';
    showServiceAreaError(address);
    return;
  }
  
  const latlng = place.geometry.location;
  
  // Move search input out of overlay and position it
  const searchInput = document.getElementById('topSearch');
  document.body.appendChild(searchInput); // Move to body so it's not hidden with overlay
  searchInput.classList.add('positioned');
  
  // Hide the overlay  
  document.getElementById('overlay').classList.add('hidden');
  
  // Show map loading overlay
  const loadingOverlay = document.getElementById('mapLoadingOverlay');
  loadingOverlay.classList.add('show');
  
  // Give the DOM more time to update the layout, then do smooth zoom animation
  setTimeout(() => {
    // Make sure map is visible and properly sized
    const mapElement = document.getElementById('map');
    if (mapElement && map) {
      // Force initial resize
      google.maps.event.trigger(map, 'resize');
      
      // Set center first
      map.setCenter(latlng);
      
      // Start with maximum zoom (closest)
      map.setZoom(25);
      
      // Smooth zoom out animation to final zoom level
      setTimeout(() => {
        // Animate zoom out to level 22 over 1 second
        let currentZoom = 25;
        const targetZoom = 22;
        const zoomStep = 0.2; // Smaller steps for smoother animation
        const stepDelay = 50; // 50ms between steps
        
        const zoomInterval = setInterval(() => {
          currentZoom -= zoomStep;
          
          if (currentZoom <= targetZoom) {
            // Final zoom level reached
            map.setZoom(targetZoom);
            google.maps.event.trigger(map, 'resize');
            clearShape();
            updateInfoBarTooltip('address-selected');
            clearInterval(zoomInterval);
            
            // Hide loading overlay after 2 seconds total
            setTimeout(() => {
              loadingOverlay.classList.add('fade-out');
              setTimeout(() => {
                loadingOverlay.classList.remove('show', 'fade-out');
              }, 500); // Wait for fade transition to complete
            }, 1000); // Show for 2 seconds total (1 second zoom + 1 second extra)
          } else {
            // Continue zooming out
            map.setZoom(currentZoom);
          }
        }, stepDelay);
      }, 200); // Start zoom animation after initial positioning
    }
  }, 250);
}

function startDrawing() {
  polyline = new google.maps.Polyline({
    map: map,
    clickable: false,
    strokeColor: '#ffffff',
    strokeWeight: 4
  });
  
  setMapCursor('crosshair');
  
  // Different event listeners based on drawing mode
  if (drawingMode === 'pin') {
    // Pin mode - click to add points
    map.addListener('click', addPoint);
    updateInfoBarTooltip('drawing-pins');
  } else {
    // Freehand mode - create drawing overlay
    createDrawingOverlay();
    updateInfoBarTooltip('drawing-freehand');
  }
  
  drawingActive = true;
  const btn = document.getElementById('toggleAreaBtn');
  btn.textContent = 'Close Area';
  btn.classList.add('active');
}

function addPoint(event) {
  path.push(event.latLng);
  polyline.setPath(path);
  
  const index = markers.length;
  const marker = new google.maps.Marker({
    position: event.latLng,
    map: map,
    draggable: true,
    icon: {
      url: flagIcon,
      scaledSize: new google.maps.Size(24, 16),
      anchor: new google.maps.Point(8, 16)
    }
  });
  
  marker.addListener('drag', () => {
    path[index] = marker.getPosition();
    (polyline || polygon).setPath(path);
    updateMeasurements();
  });
  
  markers.push(marker);
}

// Create drawing overlay using canvas
function createDrawingOverlay() {
  // Store current map state
  mapBounds = map.getBounds();
  mapZoom = map.getZoom();
  
  // Create canvas overlay
  const mapDiv = map.getDiv();
  const rect = mapDiv.getBoundingClientRect();
  
  drawingCanvas = document.createElement('canvas');
  drawingCanvas.width = rect.width;
  drawingCanvas.height = rect.height;
  drawingCanvas.style.position = 'absolute';
  drawingCanvas.style.top = '0';
  drawingCanvas.style.left = '0';
  drawingCanvas.style.zIndex = '1000';
  drawingCanvas.style.cursor = 'crosshair';
  drawingCanvas.style.backgroundColor = 'rgba(0,0,0,0.1)';
  
  mapDiv.appendChild(drawingCanvas);
  drawingContext = drawingCanvas.getContext('2d');
  
  // Disable map interactions
  map.setOptions({ 
    draggable: false,
    zoomControl: false,
    scrollwheel: false,
    disableDoubleClickZoom: true
  });
  
  // Setup canvas drawing events
  setupCanvasDrawing();
  
  // Reset drawing state
  freehandPoints = [];
  isDrawingFreehand = false;
}

// Setup canvas drawing events
function setupCanvasDrawing() {
  // Mouse events
  drawingCanvas.addEventListener('mousedown', startCanvasDrawing);
  drawingCanvas.addEventListener('mousemove', continueCanvasDrawing);
  drawingCanvas.addEventListener('mouseup', endCanvasDrawing);
  
  // Touch events
  drawingCanvas.addEventListener('touchstart', startCanvasTouchDrawing);
  drawingCanvas.addEventListener('touchmove', continueCanvasTouchDrawing);
  drawingCanvas.addEventListener('touchend', endCanvasTouchDrawing);
  
  // Prevent context menu
  drawingCanvas.addEventListener('contextmenu', (e) => e.preventDefault());
  
  // Allow clicks on control buttons to pass through
  drawingCanvas.addEventListener('click', (e) => {
    // Check if the click is on a control button area
    const rect = drawingCanvas.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const canvasHeight = rect.height;
    
    // If click is in the bottom area where controls are, don't prevent it
    if (clickY > canvasHeight - 120) { // Bottom 120px where controls are
      e.stopPropagation();
      return;
    }
  });
}

// Canvas drawing functions
function startCanvasDrawing(e) {
  e.preventDefault();
  isDrawingFreehand = true;
  
  const rect = drawingCanvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  
  freehandPoints = [{ x, y }];
  
  drawingContext.strokeStyle = '#ffffff';
  drawingContext.lineWidth = 5;
  drawingContext.lineCap = 'round';
  drawingContext.beginPath();
  drawingContext.moveTo(x, y);
  
  // Start haptic feedback for mobile devices
  startHapticFeedback();
}

function continueCanvasDrawing(e) {
  if (!isDrawingFreehand) return;
  
  e.preventDefault();
  const rect = drawingCanvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  
  // Add point to array
  freehandPoints.push({ x, y });
  
  // Use quadratic curve for smooth lines (like Google Lens)
  const len = freehandPoints.length;
  if (len >= 3) {
    // Get the last three points
    const p1 = freehandPoints[len - 3];
    const p2 = freehandPoints[len - 2];
    const p3 = freehandPoints[len - 1];
    
    // Calculate control point for smooth curve
    const cp1x = p1.x + (p2.x - p1.x) * 0.5;
    const cp1y = p1.y + (p2.y - p1.y) * 0.5;
    const cp2x = p2.x;
    const cp2y = p2.y;
    
    // Clear and redraw the entire path with smooth curves
    redrawSmoothPath();
  } else {
    // For first few points, just draw a line
    drawingContext.lineTo(x, y);
    drawingContext.stroke();
  }
}

// Redraw the entire path with smooth curves
function redrawSmoothPath() {
  if (freehandPoints.length < 2) return;
  
  // Clear the canvas
  drawingContext.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);
  
  // Set drawing style
  drawingContext.strokeStyle = '#ffffff';
  drawingContext.lineWidth = 5;
  drawingContext.lineCap = 'round';
  drawingContext.lineJoin = 'round';
  
  drawingContext.beginPath();
  drawingContext.moveTo(freehandPoints[0].x, freehandPoints[0].y);
  
  // Draw smooth curves between points
  for (let i = 1; i < freehandPoints.length - 1; i++) {
    const current = freehandPoints[i];
    const next = freehandPoints[i + 1];
    
    // Calculate control point for smooth curve
    const cpx = (current.x + next.x) / 2;
    const cpy = (current.y + next.y) / 2;
    
    drawingContext.quadraticCurveTo(current.x, current.y, cpx, cpy);
  }
  
  // Draw to the last point
  if (freehandPoints.length > 1) {
    const lastPoint = freehandPoints[freehandPoints.length - 1];
    drawingContext.lineTo(lastPoint.x, lastPoint.y);
  }
  
  drawingContext.stroke();
}

function endCanvasDrawing(e) {
  if (!isDrawingFreehand) return;
  
  e.preventDefault();
  isDrawingFreehand = false;
  
  // Stop haptic feedback
  stopHapticFeedback();
  
  // Close the shape smoothly
  if (freehandPoints.length > 2) {
    // Add the first point to close the shape
    freehandPoints.push(freehandPoints[0]);
    
    // Redraw the complete smooth path
    redrawSmoothPath();
    
    // Draw a smooth connection to close the shape
    drawingContext.beginPath();
    const len = freehandPoints.length;
    const lastPoint = freehandPoints[len - 2];
    const firstPoint = freehandPoints[0];
    
    drawingContext.moveTo(lastPoint.x, lastPoint.y);
    drawingContext.lineTo(firstPoint.x, firstPoint.y);
    drawingContext.stroke();
  }
  
  // Convert canvas coordinates to map coordinates
  convertCanvasToMapCoordinates();
}

// Touch drawing functions
function startCanvasTouchDrawing(e) {
  e.preventDefault();
  if (e.touches.length > 0) {
    const touch = e.touches[0];
    const mouseEvent = { 
      clientX: touch.clientX, 
      clientY: touch.clientY,
      preventDefault: () => {}
    };
    startCanvasDrawing(mouseEvent);
  }
}

function continueCanvasTouchDrawing(e) {
  e.preventDefault();
  if (e.touches.length > 0) {
    const touch = e.touches[0];
    const mouseEvent = { 
      clientX: touch.clientX, 
      clientY: touch.clientY,
      preventDefault: () => {}
    };
    continueCanvasDrawing(mouseEvent);
  }
}

function endCanvasTouchDrawing(e) {
  e.preventDefault();
  endCanvasDrawing({ preventDefault: () => {} });
}

// Convert canvas pixel coordinates to map lat/lng coordinates
function convertCanvasToMapCoordinates() {
  if (!freehandPoints.length || !mapBounds) return;
  
  // Convert canvas coordinates to map coordinates
  const canvasWidth = drawingCanvas.width;
  const canvasHeight = drawingCanvas.height;
  
  const ne = mapBounds.getNorthEast();
  const sw = mapBounds.getSouthWest();
  
  const latRange = ne.lat() - sw.lat();
  const lngRange = ne.lng() - sw.lng();
  
  // Convert to lat/lng for creating actual map polygon
  const mapPoints = freehandPoints.map(point => {
    const lat = ne.lat() - (point.y / canvasHeight) * latRange;
    const lng = sw.lng() + (point.x / canvasWidth) * lngRange;
    return new google.maps.LatLng(lat, lng);
  });
  
  // Create actual Google Maps polygon that moves with the map
  if (mapPoints.length >= 3) {
    polygon = new google.maps.Polygon({
      paths: mapPoints,
      strokeColor: '#2e7d32',
      strokeWeight: 3,
      fillColor: '#2e7d32',
      fillOpacity: 0.2,
      map: map
    });
    
    // Calculate area using Google Maps geometry
    const areaMeters = google.maps.geometry.spherical.computeArea(mapPoints);
    const sqFt = Math.round(areaMeters * 10.7639);
    estimatedSqFt = sqFt;
    
    estimatedPrice = Math.round(sqFt <= CONFIG.AREA_CUTOFF ? 
      CONFIG.BASE_PRICE : 
      CONFIG.BASE_PRICE + (sqFt - CONFIG.AREA_CUTOFF) * CONFIG.ADDITIONAL_RATE
    );
    
    // Show calculated area briefly, then completion message
    updateInfoBarTooltip('area-calculated', { sqFt, price: estimatedPrice });
    
    // Set service address using map center
    const centerLat = mapPoints.reduce((sum, p) => sum + p.lat(), 0) / mapPoints.length;
    const centerLng = mapPoints.reduce((sum, p) => sum + p.lng(), 0) / mapPoints.length;
    
    geocoder.geocode({ location: { lat: centerLat, lng: centerLng } }, (results, status) => {
      serviceAddress = (status === 'OK' && results[0])
        ? results[0].formatted_address
        : 'Address unavailable';
      document.getElementById('serviceAddress').textContent = serviceAddress;
    });
    
    // Create visual markers along the polygon perimeter
    createEvenlySpacedMarkers(mapPoints, Math.min(Math.max(5, Math.floor(mapPoints.length / 10)), 15));
  }
  
  // Center and fit map to the drawn shape
  centerMapOnDrawnShape();
  
  // Remove the canvas overlay completely since we now have a real polygon
  removeDrawingOverlay();
  
  // Finish the drawing process
  drawingActive = false;
  const btn = document.getElementById('toggleAreaBtn');
  btn.textContent = 'Draw Area';
  btn.classList.remove('active');
  document.getElementById('statusBar').classList.remove('disabled');
}

// Place flag markers directly on the canvas
function placeCanvasFlags() {
  if (freehandPoints.length < 3) return;
  
  // Simplify the path to get more evenly distributed points
  const simplifiedPoints = simplifyCanvasPath(freehandPoints, 15); // Higher tolerance for smoother distribution
  
  // Calculate total perimeter length in pixels using simplified path
  let totalPixelDistance = 0;
  for (let i = 0; i < simplifiedPoints.length - 1; i++) {
    const dx = simplifiedPoints[i+1].x - simplifiedPoints[i].x;
    const dy = simplifiedPoints[i+1].y - simplifiedPoints[i].y;
    totalPixelDistance += Math.sqrt(dx * dx + dy * dy);
  }
  
  // Determine number of flags based on perimeter (fewer flags for smoother look)
  const flagCount = Math.min(Math.max(5, Math.floor(totalPixelDistance / 80)), 15); // Reduced density
  const flagSpacing = totalPixelDistance / flagCount;
  
  // Place flags along the simplified path
  let currentDistance = 0;
  let targetDistance = flagSpacing / 2; // Start with half spacing
  
  for (let i = 0; i < simplifiedPoints.length - 1; i++) {
    const start = simplifiedPoints[i];
    const end = simplifiedPoints[i + 1];
    
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    
    while (targetDistance <= currentDistance + segmentLength) {
      // Calculate flag position
      const segmentProgress = (targetDistance - currentDistance) / segmentLength;
      const flagX = start.x + dx * segmentProgress;
      const flagY = start.y + dy * segmentProgress;
      
      // Draw flag on canvas
      drawCanvasFlag(flagX, flagY);
      
      targetDistance += flagSpacing;
    }
    
    currentDistance += segmentLength;
  }
}

// Simplify canvas path for better flag distribution
function simplifyCanvasPath(points, tolerance) {
  if (points.length <= 2) return points;
  
  // Find the point with the maximum distance from the line between start and end
  let maxDistance = 0;
  let maxIndex = 0;
  
  const start = points[0];
  const end = points[points.length - 1];
  
  for (let i = 1; i < points.length - 1; i++) {
    const distance = pointToLineDistanceCanvas(points[i], start, end);
    
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  
  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const firstPart = simplifyCanvasPath(points.slice(0, maxIndex + 1), tolerance);
    const secondPart = simplifyCanvasPath(points.slice(maxIndex), tolerance);
    
    // Concatenate the two parts (removing duplicate point)
    return firstPart.slice(0, -1).concat(secondPart);
  } else {
    // Distance is less than tolerance, use just the end points
    return [start, end];
  }
}

// Calculate perpendicular distance from point to line in canvas coordinates
function pointToLineDistanceCanvas(point, lineStart, lineEnd) {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;
  
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) return Math.sqrt(A * A + B * B);
  
  let param = dot / lenSq;
  
  let xx, yy;
  
  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }
  
  const dx = point.x - xx;
  const dy = point.y - yy;
  
  return Math.sqrt(dx * dx + dy * dy);
}

// Draw a flag marker on the canvas
function drawCanvasFlag(x, y) {
  const ctx = drawingContext;
  
  // Save current state
  ctx.save();
  
  // Draw flag pole
  ctx.strokeStyle = '#666';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y - 20);
  ctx.stroke();
  
  // Draw flag
  ctx.fillStyle = '#FFD700';
  ctx.strokeStyle = '#E6C200';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y - 20);
  ctx.lineTo(x + 15, y - 15);
  ctx.lineTo(x, y - 10);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw flag base (small circle)
  ctx.fillStyle = '#333';
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, 2 * Math.PI);
  ctx.fill();
  
  // Restore state
  ctx.restore();
}

// Center and fit map to the drawn shape
function centerMapOnDrawnShape() {
  if (!freehandPoints.length || !mapBounds) return;
  
  // Convert canvas coordinates to map coordinates to find bounds
  const canvasWidth = drawingCanvas.width;
  const canvasHeight = drawingCanvas.height;
  
  const ne = mapBounds.getNorthEast();
  const sw = mapBounds.getSouthWest();
  
  const latRange = ne.lat() - sw.lat();
  const lngRange = ne.lng() - sw.lng();
  
  // Find min/max coordinates of the drawn shape
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  
  freehandPoints.forEach(point => {
    const lat = ne.lat() - (point.y / canvasHeight) * latRange;
    const lng = sw.lng() + (point.x / canvasWidth) * lngRange;
    
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });
  
  // Create bounds for the drawn shape with some padding
  const shapeBounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(minLat, minLng),
    new google.maps.LatLng(maxLat, maxLng)
  );
  
  // Fit map to the shape with padding
  map.fitBounds(shapeBounds, {
    top: 50,
    right: 50,
    bottom: 50,
    left: 50
  });
}

// Calculate area from the canvas path
function calculateAreaFromCanvasPath() {
  if (!freehandPoints.length || !mapBounds) return;
  
  // Convert canvas path to map coordinates for area calculation only
  const canvasWidth = drawingCanvas.width;
  const canvasHeight = drawingCanvas.height;
  
  const ne = mapBounds.getNorthEast();
  const sw = mapBounds.getSouthWest();
  
  const latRange = ne.lat() - sw.lat();
  const lngRange = ne.lng() - sw.lng();
  
  // Convert to lat/lng for area calculation
  const mapPoints = freehandPoints.map(point => {
    const lat = ne.lat() - (point.y / canvasHeight) * latRange;
    const lng = sw.lng() + (point.x / canvasWidth) * lngRange;
    return new google.maps.LatLng(lat, lng);
  });
  
  // Calculate area using Google Maps geometry
  if (mapPoints.length >= 3) {
    const areaMeters = google.maps.geometry.spherical.computeArea(mapPoints);
    const sqFt = Math.round(areaMeters * 10.7639);
    estimatedSqFt = sqFt;
    
    estimatedPrice = Math.round(sqFt <= CONFIG.AREA_CUTOFF ? 
      CONFIG.BASE_PRICE : 
      CONFIG.BASE_PRICE + (sqFt - CONFIG.AREA_CUTOFF) * CONFIG.ADDITIONAL_RATE
    );
    
    document.getElementById('infoBar').textContent =
      `Area: ${sqFt} sq ft | Estimated Price: $${estimatedPrice}`;
    
    // Set service address using map center
    const centerLat = mapPoints.reduce((sum, p) => sum + p.lat(), 0) / mapPoints.length;
    const centerLng = mapPoints.reduce((sum, p) => sum + p.lng(), 0) / mapPoints.length;
    
    geocoder.geocode({ location: { lat: centerLat, lng: centerLng } }, (results, status) => {
      serviceAddress = (status === 'OK' && results[0])
        ? results[0].formatted_address
        : 'Address unavailable';
      document.getElementById('serviceAddress').textContent = serviceAddress;
    });
  }
}

// Remove drawing event listeners but keep canvas
function removeDrawingEventListeners() {
  if (!drawingCanvas) return;
  
  // Remove drawing events
  drawingCanvas.removeEventListener('mousedown', startCanvasDrawing);
  drawingCanvas.removeEventListener('mousemove', continueCanvasDrawing);
  drawingCanvas.removeEventListener('mouseup', endCanvasDrawing);
  
  drawingCanvas.removeEventListener('touchstart', startCanvasTouchDrawing);
  drawingCanvas.removeEventListener('touchmove', continueCanvasTouchDrawing);
  drawingCanvas.removeEventListener('touchend', endCanvasTouchDrawing);
  
  // Enable zoom controls but keep map dragging disabled to lock the view
  map.setOptions({ 
    draggable: false, // Keep panning locked
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false
  });
}

// Remove the drawing overlay (only when clearing completely)
function removeDrawingOverlay() {
  if (drawingCanvas) {
    drawingCanvas.remove();
    drawingCanvas = null;
    drawingContext = null;
  }
  
  // Re-enable map interactions
  map.setOptions({ 
    draggable: true,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false
  });
}

// Simplify a path using Douglas-Peucker algorithm
function simplifyPath(points, tolerance) {
  if (points.length <= 2) return points;
  
  // Find the point with the maximum distance
  let maxDistance = 0;
  let maxIndex = 0;
  
  const start = points[0];
  const end = points[points.length - 1];
  
  for (let i = 1; i < points.length - 1; i++) {
    const distance = pointToLineDistance(points[i], start, end);
    
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  
  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    // Recursive simplification
    const firstPart = simplifyPath(points.slice(0, maxIndex + 1), tolerance);
    const secondPart = simplifyPath(points.slice(maxIndex), tolerance);
    
    // Concatenate the two parts (removing duplicate point)
    return firstPart.slice(0, -1).concat(secondPart);
  } else {
    // Distance is less than tolerance, use just the end points
    return [start, end];
  }
}

// Calculate the perpendicular distance from a point to a line segment
function pointToLineDistance(point, lineStart, lineEnd) {
  // Use the Google Maps geometry library to calculate distances
  if (!lineStart || !lineEnd || !point) return 0;
  
  // Convert to cartesian coordinates for easier calculation
  const p = latLngToCartesian(point);
  const s = latLngToCartesian(lineStart);
  const e = latLngToCartesian(lineEnd);
  
  // Vector from start to end
  const line = {
    x: e.x - s.x,
    y: e.y - s.y,
    z: e.z - s.z
  };
  
  // Vector from start to point
  const pointVector = {
    x: p.x - s.x,
    y: p.y - s.y,
    z: p.z - s.z
  };
  
  // Calculate the squared length of the line
  const lineLengthSquared = line.x * line.x + line.y * line.y + line.z * line.z;
  
  if (lineLengthSquared === 0) {
    // Start and end are the same point
    return Math.sqrt(
      Math.pow(p.x - s.x, 2) + 
      Math.pow(p.y - s.y, 2) + 
      Math.pow(p.z - s.z, 2)
    );
  }
  
  // Calculate dot product
  const dotProduct = 
    pointVector.x * line.x + 
    pointVector.y * line.y + 
    pointVector.z * line.z;
  
  // Calculate the projection coefficient
  const projCoeff = dotProduct / lineLengthSquared;
  
  // Calculate projection point
  const proj = {
    x: s.x + projCoeff * line.x,
    y: s.y + projCoeff * line.y,
    z: s.z + projCoeff * line.z
  };
  
  // If projection is outside the line segment
  if (projCoeff < 0) {
    // Point is closest to start
    return Math.sqrt(
      Math.pow(p.x - s.x, 2) + 
      Math.pow(p.y - s.y, 2) + 
      Math.pow(p.z - s.z, 2)
    );
  } else if (projCoeff > 1) {
    // Point is closest to end
    return Math.sqrt(
      Math.pow(p.x - e.x, 2) + 
      Math.pow(p.y - e.y, 2) + 
      Math.pow(p.z - e.z, 2)
    );
  }
  
  // Calculate distance from point to projection
  return Math.sqrt(
    Math.pow(p.x - proj.x, 2) + 
    Math.pow(p.y - proj.y, 2) + 
    Math.pow(p.z - proj.z, 2)
  );
}

// Convert LatLng to Cartesian coordinates on a unit sphere
function latLngToCartesian(latLng) {
  const lat = latLng.lat() * Math.PI / 180;
  const lng = latLng.lng() * Math.PI / 180;
  
  return {
    x: Math.cos(lat) * Math.cos(lng),
    y: Math.cos(lat) * Math.sin(lng),
    z: Math.sin(lat)
  };
}

// Create evenly spaced markers around the perimeter
function createEvenlySpacedMarkers(points, count) {
  // Calculate total perimeter length
  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    totalDistance += google.maps.geometry.spherical.computeDistanceBetween(
      points[i], points[i+1]
    );
  }
  
  // Distance between markers
  const segmentLength = totalDistance / count;
  
  // Clear existing markers and path
  markers.forEach(marker => marker.setMap(null));
  markers = [];
  path = [];
  
  // Add first marker
  addMarkerAtPoint(points[0]);
  
  // Add remaining markers
  let distanceSoFar = 0;
  let targetDistance = segmentLength;
  
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i+1];
    
    const segmentDistance = google.maps.geometry.spherical.computeDistanceBetween(
      start, end
    );
    
    if (distanceSoFar + segmentDistance < targetDistance) {
      // This segment is too short to reach next marker
      distanceSoFar += segmentDistance;
      continue;
    }
    
    // We need to add one or more markers within this segment
    while (targetDistance <= distanceSoFar + segmentDistance) {
      // Calculate the position of the marker within the segment
      const fraction = (targetDistance - distanceSoFar) / segmentDistance;
      
      const lat = start.lat() + fraction * (end.lat() - start.lat());
      const lng = start.lng() + fraction * (end.lng() - start.lng());
      
      // Add a marker at this position
      const point = new google.maps.LatLng(lat, lng);
      addMarkerAtPoint(point);
      
      // Increment target for next marker
      targetDistance += segmentLength;
    }
    
    // Update distance so far
    distanceSoFar += segmentDistance;
  }
  
  // If we need more markers, add one at the end point
  if (markers.length < 3) {
    addMarkerAtPoint(points[points.length - 1]);
  }
}

// Add a marker at the specified point
function addMarkerAtPoint(latLng) {
  const index = markers.length;
  
  // Add to path
  path.push(latLng);
  
  // Create a marker
  const marker = new google.maps.Marker({
    position: latLng,
    map: map,
    draggable: true,
    icon: {
      url: flagIcon,
      scaledSize: new google.maps.Size(24, 16),
      anchor: new google.maps.Point(8, 16)
    }
  });
  
  // Add drag listener
  marker.addListener('drag', () => {
    path[index] = marker.getPosition();
    if (polygon) {
      polygon.setPath(path);
      updateMeasurements();
    } else if (polyline) {
      polyline.setPath(path);
    }
  });
  
  markers.push(marker);
}

// Finish drawing with the existing path (legacy function for pin mode)
function finishDrawingWithExistingPath() {
  if (path.length < 3) return;
  
  if (polyline) {
    polyline.setMap(null);
    polyline = null;
  }
  
  google.maps.event.clearListeners(map, 'click');
  
  polygon = new google.maps.Polygon({
    paths: path,
    strokeColor: '#D00',
    strokeWeight: 2,
    fillColor: '#D00',
    fillOpacity: 0.3,
    map: map
  });
  
  updateMeasurements();
  geocodePolygonCenter();
  
  // Show area calculation briefly, then completion message for pin mode
  updateInfoBarTooltip('area-calculated', { 
    sqFt: estimatedSqFt, 
    price: estimatedPrice 
  });
  
  drawingActive = false;
  const btn = document.getElementById('toggleAreaBtn');
  btn.textContent = 'Draw Area';
  btn.classList.remove('active');
  document.getElementById('statusBar').classList.remove('disabled');
}

function finishDrawing() {
  // Stop haptic feedback when finishing drawing
  stopHapticFeedback();
  
  if (drawingMode === 'freehand') {
    // Force end any active drawing first
    if (isDrawingFreehand) {
      isDrawingFreehand = false;
      // If we have points, convert them
      if (freehandPoints.length >= 3) {
        convertCanvasToMapCoordinates();
        return; // convertCanvasToMapCoordinates will call finishDrawingWithExistingPath
      }
    }
    // Clean up canvas overlay
    removeDrawingOverlay();
  } else {
    // Standard pin mode - just clear the click listener
    google.maps.event.clearListeners(map, 'click');
  }
  
  if (path.length < 3) {
    // Not enough points, just clean up
    drawingActive = false;
    const btn = document.getElementById('toggleAreaBtn');
    btn.textContent = 'Draw Area';
    btn.classList.remove('active');
    return;
  }
  
  if (polyline) {
    polyline.setMap(null);
    polyline = null;
  }
  
  polygon = new google.maps.Polygon({
    paths: path,
    strokeColor: '#D00',
    strokeWeight: 2,
    fillColor: '#D00',
    fillOpacity: 0.3,
    map: map
  });
  
  updateMeasurements();
  geocodePolygonCenter();
  
  drawingActive = false;
  const btn = document.getElementById('toggleAreaBtn');
  btn.textContent = 'Draw Area';
  btn.classList.remove('active');
  document.getElementById('statusBar').classList.remove('disabled');
}

function geocodePolygonCenter() {
  const points = polygon.getPath().getArray();
  let latSum = 0, lngSum = 0;
  
  points.forEach(point => {
    latSum += point.lat();
    lngSum += point.lng();
  });
  
  const center = { lat: latSum / points.length, lng: lngSum / points.length };
  
  geocoder.geocode({ location: center }, (results, status) => {
    serviceAddress = (status === 'OK' && results[0])
      ? results[0].formatted_address
      : 'Address unavailable';
    document.getElementById('serviceAddress').textContent = serviceAddress;
  });
}

function clearShape() {
  if (polyline) polyline.setMap(null);
  if (polygon) polygon.setMap(null);
  
  markers.forEach(marker => marker.setMap(null));
  markers = [];
  path = [];
  freehandPoints = [];
  
  updateInfoBarTooltip('initial');
  
  const statusBar = document.getElementById('statusBar');
  statusBar.textContent = 'Choose Services';
  statusBar.classList.add('disabled');
  
  setMapCursor(null);
  google.maps.event.clearListeners(map, 'click');
  
  // Clean up canvas overlay completely when clearing
  removeDrawingOverlay();
  
  // Re-enable map dragging when clearing (unlocks the map)
  if (map) {
    map.setOptions({ 
      draggable: true,
      zoomControl: true,
      scrollwheel: true,
      disableDoubleClickZoom: false
    });
  }
  
  isDrawingFreehand = false;
  drawingActive = false;
  
  const btn = document.getElementById('toggleAreaBtn');
  btn.textContent = 'Draw Area';
  btn.classList.remove('active');
}

function updateMeasurements() {
  const areaMeters = polygon
    ? google.maps.geometry.spherical.computeArea(polygon.getPath())
    : google.maps.geometry.spherical.computeArea(polyline.getPath());
  
  const sqFt = Math.round(areaMeters * 10.7639);
  estimatedSqFt = sqFt;
  
  estimatedPrice = Math.round(sqFt <= CONFIG.AREA_CUTOFF ? 
    CONFIG.BASE_PRICE : 
    CONFIG.BASE_PRICE + (sqFt - CONFIG.AREA_CUTOFF) * CONFIG.ADDITIONAL_RATE
  );
  
  document.getElementById('infoBar').textContent =
    `Area: ${sqFt} sq ft | Estimated Price: $${estimatedPrice}`;
}

function showServicesOverlay() {
  document.getElementById('serviceAddress').textContent = serviceAddress;
  populateDates();
  document.getElementById('servicesOverlay').classList.remove('hidden');
}

function populateDates() {
  const container = document.getElementById('datePicker');
  container.innerHTML = '';
  const today = new Date();
  
  for (let i = 2; i < 16; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    
    const button = document.createElement('button');
    button.className = 'date-btn';
    button.textContent = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    button.dataset.date = date.toISOString().slice(0, 10);
    
    button.addEventListener('click', () => {
      document.querySelectorAll('.date-btn').forEach(btn => 
        btn.classList.remove('selected')
      );
      button.classList.add('selected');
    });
    
    container.appendChild(button);
  }
}

// Video Modal functionality
function initVideoModal() {
  const videoModal = document.getElementById('videoModal');
  const modalVideo = document.getElementById('modalVideo');
  const videoClose = document.getElementById('videoClose');
  const storyCircles = document.querySelectorAll('.story-circle');
  
  // Video sources mapping
  const videoSources = {
    'standard': 'https://cdn.shopify.com/videos/c/o/v/5c38632aa6bd467286a1d75233b50195.mp4', // Replace with actual video URLs
    'mulch': 'https://cdn.shopify.com/videos/c/o/v/5c999ca24deb42139f251a50c29d9297.mp4',
    'sod': 'https://cdn.shopify.com/videos/c/o/v/7b2acf1237634782b252ca3b87d14412.mp4'
  };
  
  // Add click listeners to story circles
  storyCircles.forEach(circle => {
    circle.addEventListener('click', () => {
      const videoType = circle.dataset.video;
      const videoSrc = videoSources[videoType];
      
      if (videoSrc) {
        modalVideo.src = videoSrc;
        videoModal.classList.add('show');
        modalVideo.play();
      }
    });
  });
  
  // Close video modal
  function closeVideoModal() {
    videoModal.classList.remove('show');
    modalVideo.pause();
    modalVideo.src = '';
  }
  
  videoClose.addEventListener('click', closeVideoModal);
  
  // Close on background click
  videoModal.addEventListener('click', (e) => {
    if (e.target === videoModal) {
      closeVideoModal();
    }
  });
  
  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && videoModal.classList.contains('show')) {
      closeVideoModal();
    }
  });
}

// Booking functionality
function initBookingOverlay() {
  const bookBtn = document.getElementById('bookServiceBtn');
  const overlay = document.getElementById('bookingOverlay');
  const closeBtn = document.getElementById('closeBookingOverlay');
  const tcCheckbox = document.getElementById('tcCheckbox');
  const finalizeBtn = document.getElementById('finalizeBookingBtn');

  bookBtn.addEventListener('click', () => overlay.classList.remove('hidden'));
  closeBtn.addEventListener('click', () => overlay.classList.add('hidden'));
  tcCheckbox.addEventListener('change', () => finalizeBtn.disabled = !tcCheckbox.checked);
  finalizeBtn.addEventListener('click', finalizeBooking);
}

function finalizeBooking() {
  const selectedDate = document.querySelector('.date-btn.selected');
  
  if (!selectedDate) {
    showBanner(`Please pick a service day for ${serviceAddress || 'your property'}.`);
    return;
  }

  hideBanner();
  
  const serviceDate = selectedDate.dataset.date;
  const shapeJSON = JSON.stringify(path.map(point => ({
    lat: point.lat(),
    lng: point.lng()
  })));

  // Shopify cart integration
  fetch('/products/landscaping-service-deposit.js')
    .then(response => response.json())
    .then(product => product.variants[0].id)
    .then(variantId => {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            'Service Date': serviceDate,
            'Service Address': serviceAddress,
            'Estimated Sq Ft': estimatedSqFt,
            'Estimated Price': `$${estimatedPrice}`
          }
        })
      });
    })
    .then(() => {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: shapeJSON })
      });
    })
    .then(() => {
      window.location.href = '/checkout';
    })
    .catch(error => {
      console.error('Checkout error:', error);
      showBanner('Sorry, there was a problem. Please try again.');
    });
}

function showBanner(message, type = 'warning') {
  const banner = document.getElementById('banner');
  const bannerMessage = document.getElementById('bannerMessage');
  
  bannerMessage.textContent = message;
  banner.className = ''; // Reset classes
  if (type === 'success') {
    banner.classList.add('success');
  } else if (type === 'info') {
    banner.classList.add('info');
  }
  banner.classList.remove('hidden');
}

function hideBanner() {
  document.getElementById('banner').classList.add('hidden');
}

// Chat functionality has been removed